import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CreditCard, Check, X, ArrowLeft, AlertTriangle, Receipt, Loader2, ExternalLink, Download, FileText, RotateCcw, ShieldCheck, Sparkles } from "lucide-react";

export default function Billing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [myInvoices, setMyInvoices] = useState([]);
  const [dbPlans, setDbPlans] = useState([]);
  const [discount, setDiscount] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const polledRef = useRef(false);

  // Contract specific states
  const [contracts, setContracts] = useState([]);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractTier, setContractTier] = useState("");
  const [contractDraft, setContractDraft] = useState(null);
  const [signatureName, setSignatureName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signing, setSigning] = useState(false);
  const [selectedViewContract, setSelectedViewContract] = useState(null);

  // New Upload / Signature States
  const [signatureType, setSignatureType] = useState("digital"); // "digital" | "physical_sig" | "contract_doc"
  const [uploadedSignatureUrl, setUploadedSignatureUrl] = useState("");
  const [uploadedContractUrl, setUploadedContractUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [contractDob, setContractDob] = useState("");
  const [contractDiscountCode, setContractDiscountCode] = useState("");


  // Parse dynamic plans configured in DB or fall back to static PLANS defaults
  const plansToRender = (dbPlans && dbPlans.length > 0)
    ? dbPlans.map((p) => {
        const isEnterprise = p.category === "enterprise" || (p.name || "").toLowerCase() === "enterprise";
        const isFreeNamed = (p.name || "").toLowerCase() === "free";
        const hasMonthly = p.monthly_price_pence != null && p.monthly_price_pence > 0;
        const hasYearly = p.yearly_price_pence != null && p.yearly_price_pence > 0;
        let monthly, annual;
        if (isEnterprise) { monthly = null; annual = null; }
        else if (isFreeNamed) { monthly = 0; annual = 0; }
        else {
          monthly = hasMonthly ? p.monthly_price_pence / 100
                   : (p.billing_cycle === "yearly" ? null : (p.price_pence > 0 ? p.price_pence / 100 : null));
          annual = hasYearly ? Math.round(p.yearly_price_pence / 12) / 100
                  : (p.billing_cycle === "yearly" && p.price_pence > 0 ? Math.round(p.price_pence / 12) / 100 : null);
        }
        return {
          tier: p.name.toLowerCase(), name: p.name, desc: p.description,
          monthly, annual,
          tag: p.tag,
          is_custom: !!p.is_custom
        };
      })
    : []; // Fall back to empty to let PLANS map below if empty
  const load = useCallback(async () => {
    setLoading(true);
    // Fetch in parallel so an Owner (no recruiter billing) still sees their invoices.
    const [bRes, iRes, pRes, cRes] = await Promise.allSettled([
      api.get("/recruiter/billing"),
      api.get("/users/me/invoices"),
      api.get("/plans/catalog?category=subscription"),
      api.get("/recruiter/contracts"),
    ]);
    if (bRes.status === "fulfilled") setBilling(bRes.value.data);
    else setError(formatApiError(bRes.reason?.response?.data?.detail) || bRes.reason?.message || "");
    if (iRes.status === "fulfilled") setMyInvoices(iRes.value.data.invoices || []);
    if (pRes.status === "fulfilled") setDbPlans(pRes.value.data.plans || []);
    if (cRes.status === "fulfilled") setContracts(cRes.value.data.contracts || []);
    setLoading(false);
  }, []);

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [load]);

  // Re-fetch billing whenever the tab regains focus — catches state changes
  // made inside the Stripe Customer Portal (e.g. user cancelled there) so the
  // banner updates immediately without a manual refresh.
  useEffect(() => {
    const onFocus = () => { load(); };
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  // Handle return from Stripe checkout (success or cancel)
  useEffect(() => {
    const sid = searchParams.get("stripe_session_id");
    const cancelled = searchParams.get("stripe_cancelled");
    if (cancelled) {
      setInfo("Checkout cancelled — no charge made.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sid || polledRef.current) return;
    polledRef.current = true;
    setInfo("Confirming your payment…");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/recruiter/subscription/status/${encodeURIComponent(sid)}`);
        if (data.status === "completed" && data.payment_status === "paid") {
          setInfo(`Subscription active: ${data.tier} (${data.cycle}). Welcome aboard.`);
          await refresh();
          load();
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === "expired" || data.payment_status === "unpaid") {
          setInfo("Checkout did not complete. Please try again.");
          setSearchParams({}, { replace: true });
          return;
        }
        if (attempts < 8) setTimeout(poll, 1500);
        else { setInfo("Still confirming… please refresh in a minute."); setSearchParams({}, { replace: true }); }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const buy = async (tier, cycle) => {
    setError(""); setInfo("");
    const isUpgrade = billing?.has_stripe_customer && billing?.current_tier !== "free";
    if (isUpgrade) {
      const ok = window.confirm(`Switch to ${tier} (${cycle})? If this is an upgrade, Stripe will charge only the prorated difference for the remainder of this billing cycle. If it's a downgrade, your current plan stays active until the period ends.`);
      if (!ok) return;
    }
    setRedirecting(true);
    try {
      const payload = {
        tier, cycle,
        origin_url: window.location.origin,
        ...(discount ? { discount_code: discount.trim().toUpperCase() } : {}),
      };
      const { data } = await api.post("/recruiter/subscription", payload);
      if (data.upgraded) {
        setInfo(data.message || `Plan upgraded to ${data.tier} (${data.cycle}). Prorated charge invoiced.`);
        await refresh();
        load();
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setError("No checkout URL received from server.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRedirecting(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep your current plan's features until the end of the billing cycle, then drop to Free.")) return;
    try {
      await api.post("/recruiter/subscription/cancel");
      await refresh();
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const resume = async () => {
    setError(""); setInfo("");
    try {
      await api.post("/recruiter/subscription/resume");
      setInfo("Subscription resumed — your plan will continue billing as normal.");
      await refresh();
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openPortal = async () => {
    setError(""); setInfo(""); setRedirecting(true);
    try {
      const { data } = await api.post("/recruiter/billing/portal", { origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
      setError("No portal URL returned.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRedirecting(false); }
  };

  const openSignContractModal = async (tier) => {
    setError(""); setInfo("");
    try {
      const { data } = await api.post("/recruiter/contracts", { tier });
      setContractDraft(data);
      setContractTier(tier);
      setSignatureName("");
      setSignerTitle("");
      setShowContractModal(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleUploadContractFile = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/recruiter/contracts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (type === "signature") {
        setUploadedSignatureUrl(data.file_url);
      } else {
        setUploadedContractUrl(data.file_url);
      }
    } catch (e) {
      setError("File upload failed: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSignContract = async () => {
    if (!signatureName.trim()) {
      alert("Please enter your name to sign the contract.");
      return;
    }
    if (!contractDob) {
      alert("Please enter your Date of Birth to verify age for contract execution.");
      return;
    }
    
    // Age 18+ check for contract signing
    const birthDate = new Date(contractDob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) {
      alert("You must be 18 years or older to execute a binding subscription contract.");
      return;
    }

    if (signatureType === "physical_sig" && !uploadedSignatureUrl) {
      alert("Please upload your physical signature image first.");
      return;
    }
    if (signatureType === "contract_doc" && !uploadedContractUrl) {
      alert("Please upload the signed contract document first.");
      return;
    }
    setSigning(true);
    setError(""); setInfo("");
    try {
      await api.post("/recruiter/contracts/sign", {
        tier: contractTier,
        signature_name: signatureName,
        signer_title: signerTitle,
        dob: contractDob,
        discount_code: contractDiscountCode || null,
        uploaded_contract_url: signatureType === "contract_doc" ? uploadedContractUrl : null,
        uploaded_signature_url: signatureType === "physical_sig" ? uploadedSignatureUrl : null,
      });
      setInfo(`12-Month Contract for ${contractTier.toUpperCase()} successfully signed and activated!`);
      setShowContractModal(false);
      setUploadedSignatureUrl("");
      setUploadedContractUrl("");
      setContractDob("");
      setContractDiscountCode("");
      setSignatureType("digital");
      await refresh();
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSigning(false);
    }
  };

  // Owner-managed plans drive /billing — same source of truth as /pricing and Stripe checkout.
  // Display rules:
  //   - Enterprise plans → "Custom" on both buttons (locked, no purchase)
  //   - Free plans → £0
  //   - Subscription plans with no price configured → "Custom"
  const FALLBACK_PLANS = [
    { tier: "starter", name: "Starter", monthly: 12.99, annual: 9.99, desc: "10 jobs · unlimited applicants · 1 Featured boost/mo · 10 team members" },
    { tier: "growth", name: "Growth", monthly: 29.99, annual: 24.99, desc: "Unlimited jobs · unlimited applicants · 3 Featured + 1 Spotlight/mo · 20 team members" },
  ];
  const PLANS = (dbPlans.length > 0 ? dbPlans : null)
    ? dbPlans
        .filter((p) => p.category === "subscription" && (p.name || "").toLowerCase() !== "enterprise")
        .map((p) => {
          const isFreeNamed = (p.name || "").toLowerCase() === "free";
          const hasMonthly = p.monthly_price_pence != null && p.monthly_price_pence > 0;
          const hasYearly = p.yearly_price_pence != null && p.yearly_price_pence > 0;
          let monthly, annual;
          if (isFreeNamed) { monthly = 0; annual = 0; }
          else {
            monthly = hasMonthly ? p.monthly_price_pence / 100
                     : (p.billing_cycle !== "yearly" && p.price_pence > 0 ? p.price_pence / 100 : null);
            const yearlyTotal = hasYearly ? p.yearly_price_pence
                              : (p.billing_cycle === "yearly" && p.price_pence > 0 ? p.price_pence : 0);
            annual = yearlyTotal > 0 ? Math.round(yearlyTotal / 12) / 100 : null;
          }
          return { tier: (p.name || "").toLowerCase(), name: p.name, monthly, annual,
                   desc: p.description || "", tag: p.tag };
        })
    : FALLBACK_PLANS;

  if (loading) return <div className="p-16">Loading…</div>;

  const pending = user && user.role === "recruiter" && !user.is_approved;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
      <Link to="/recruiter" className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <div className="mt-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><CreditCard className="w-3 h-3" /> Billing</div>
          <h1 className="font-display font-black text-5xl mt-3 tracking-tight">Manage billing.</h1>
          {user?.account_number && (
            <div className="mt-2 text-xs font-mono text-[#5C5C5C] bg-[#F3F2EB] px-2.5 py-1 inline-block border border-[#E2E0D8]" data-testid="user-account-number">
              Account No: <strong>{user.account_number}</strong>
            </div>
          )}
        </div>
      </div>

      {pending && (
        <div data-testid="billing-pending" className="mt-6 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
          <div className="text-sm">
            Your account is pending owner approval. <strong>No payment will be taken</strong> until you&apos;re approved.
          </div>
        </div>
      )}

      <section className="mt-8 border border-[#0F0F0F] p-6">
        <div className="label-eyebrow">Current plan</div>
        <div className="flex items-end justify-between flex-wrap gap-3 mt-2">
          <div>
            <div className="font-display font-black text-3xl capitalize">{billing?.plan?.label || billing?.current_tier}</div>
            <div className="text-sm text-[#5C5C5C] mt-1">
              {billing?.current_tier === "free"
                ? "2 active jobs · 100 applicants/job/month"
                : `Billed ${billing?.current_cycle} · £${billing?.current_cycle === "annual" ? billing?.plan?.annual_price : billing?.plan?.monthly_price}/mo`}
            </div>
            {billing?.current_tier !== "free" && billing?.current_period_end && !billing?.pending_downgrade && !billing?.fully_cancelled && (
              <div data-testid="renewal-date" className="text-sm mt-1 inline-flex items-center gap-1.5 text-[#0F0F0F]">
                <RotateCcw className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
                <span>Next renewal: <strong>{new Date(billing.current_period_end).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</strong></span>
              </div>
            )}
          </div>
          {billing?.current_tier !== "free" && (
            <div className="flex gap-2 flex-wrap">
              {billing?.has_stripe_customer && (
                <button onClick={openPortal} disabled={redirecting} data-testid="open-portal" className="btn-ink text-sm disabled:opacity-50">
                  {redirecting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening…</> : <><ExternalLink className="w-4 h-4" /> Manage card & invoices</>}
                </button>
              )}
              {!billing?.cancel_at_period_end && (
                <button onClick={cancel} data-testid="cancel-sub" className="btn-ghost text-sm">
                  <X className="w-4 h-4" /> Cancel subscription
                </button>
              )}
            </div>
          )}
        </div>

        {billing?.fully_cancelled && (
          <div data-testid="fully-cancelled-banner" className="mt-5 border-2 border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <X className="w-5 h-5 text-[var(--brand-primary)] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-[var(--brand-primary)]">
                  Subscription cancelled — reverted to Free.
                </div>
                <div className="text-xs text-[#5C5C5C] mt-1">
                  Your previous billing period has ended (or your subscription was cancelled directly in Stripe). Pick a new plan below to restore paid features.
                </div>
              </div>
            </div>
          </div>
        )}

        {billing?.pending_downgrade && (
          <div data-testid="pending-downgrade-banner" className="mt-5 border-2 border-amber-500 bg-amber-50 p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-amber-900">
                  {billing?.cancel_at_period_end
                    ? `Cancellation pending — your ${billing?.tier_until_period_end || billing?.current_tier} plan stays active until ${new Date(billing.cancel_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}.`
                    : `${(billing?.tier_until_period_end || "").replace(/^./, (c) => c.toUpperCase())} plan stays active until ${new Date(billing.cancel_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}, then switches to ${billing.current_tier}.`}
                </div>
                <div className="text-xs text-amber-800 mt-1">
                  You keep every feature you paid for until that date — nothing changes until then.
                </div>
              </div>
              {billing?.cancel_at_period_end && (
                <button onClick={resume} data-testid="resume-sub" className="btn-terra text-sm">
                  <RotateCcw className="w-3.5 h-3.5" /> Resume subscription
                </button>
              )}
            </div>
          </div>
        )}

        {billing?.effective_plan && (
          <div data-testid="effective-quotas" className="mt-5">
            <div className="label-eyebrow flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> Your active limits
              {Object.keys(billing?.account_overrides || {}).length > 0 && (
                <span data-testid="custom-quota-badge" className="inline-flex items-center gap-1 ml-1 bg-[var(--brand-primary)] text-white text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5">
                  <Sparkles className="w-2.5 h-2.5" /> Custom
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              {[
                { key: "max_active_jobs", label: "Active jobs", ovKey: "max_jobs" },
                { key: "max_applications_per_month", label: "Applications/mo", ovKey: "max_applications_per_month" },
                { key: "team_members", label: "Team seats", ovKey: "max_team_seats" },
                { key: "max_featured_boosts_per_month", fallbackKey: "featured_boosts_per_month", label: "Featured boosts/mo", ovKey: "max_featured_boosts_per_month" },
                { key: "max_spotlight_boosts_per_month", fallbackKey: "premium_boosts_per_month", label: "Spotlight boosts/mo", ovKey: "max_spotlight_boosts_per_month" },
              ].map((q) => {
                const raw = billing.effective_plan[q.key] ?? billing.effective_plan[q.fallbackKey];
                const overridden = (billing.account_overrides || {})[q.ovKey] != null;
                const isPlanUpdated = billing.plan && raw !== (billing.plan[q.key] ?? billing.plan[q.fallbackKey]);
                const isHighlighted = overridden || isPlanUpdated;
                if (raw == null) return null;
                const display = raw === -1 ? "Unlimited" : raw;
                const setBy = billing.account_overrides_updated_by_name || "the platform owner";
                const setAt = billing.account_overrides_updated_at
                  ? new Date(billing.account_overrides_updated_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
                  : "";
                const note = billing.account_overrides_note || "";
                const tooltipParts = [overridden ? `Custom quota set by ${setBy}` : `Plan limit modified by the platform owner`];
                if (overridden && setAt) tooltipParts.push(`on ${setAt}`);
                if (overridden && note) tooltipParts.push(`— "${note}"`);
                const tooltip = isHighlighted ? tooltipParts.join(" ") : "";
                return (
                  <div key={q.label} data-testid={`quota-${q.ovKey}`} title={tooltip}
                       className={`border ${isHighlighted ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 cursor-help" : "border-[#E2E0D8]"} p-3 group relative`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">{q.label}</div>
                    <div className="font-display font-black text-2xl mt-0.5">{display}</div>
                    {isHighlighted && (
                      <>
                        <div className="text-[10px] text-[var(--brand-primary)] font-semibold uppercase tracking-widest mt-0.5">
                          {overridden ? "Owner-customised" : "Plan Customized"}
                        </div>
                        {/* Hover tooltip — appears beneath the card */}
                        <div data-testid={`quota-tooltip-${q.ovKey}`} className="hidden group-hover:block absolute left-0 right-0 top-full mt-1 z-20 bg-[#0F0F0F] text-[#FDFCF8] text-xs p-2.5 shadow-lg leading-snug text-left">
                          <div className="font-semibold">{overridden ? "Custom quota" : "Custom Plan Limit"}</div>
                          <div className="mt-0.5 opacity-90">{overridden ? `Set by ${setBy}${setAt ? ` · ${setAt}` : ""}` : "This limit has been customized from platform defaults by the owner."}</div>
                          {overridden && note && <div className="mt-1 italic opacity-75">&ldquo;{note}&rdquo;</div>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#5C5C5C] mt-2 italic">Custom limits set by the platform owner are emphasised on the quota page — these supersede your plan&apos;s defaults.</p>
          </div>
        )}

        {billing?.effective_features && Object.keys(billing.effective_features).length > 0 && (
          <div data-testid="effective-features" className="mt-6">
            <div className="label-eyebrow flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Paid-tier features
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {[
                { key: "custom_email_templates", label: "Custom email templates" },
                { key: "custom_careers_branding", label: "Custom careers page branding" },
                { key: "bulk_actions", label: "Bulk applicant actions" },
                { key: "whitelabel", label: "White-label mode" },
                { key: "custom_pipeline", label: "Custom recruitment pipeline" },
                { key: "company_tracking", label: "Company progress tracking" },
              ].map((f) => {
                if (!(f.key in billing.effective_features)) return null;
                const granted = !!billing.effective_features[f.key];
                const ffOverride = (billing.account_overrides?.feature_flags || {})[f.key];
                const isOverridden = ffOverride === true || ffOverride === false;
                const minTier = billing.tier_feature_defaults?.[f.key] || "";
                const tooltip = isOverridden
                  ? `Owner-${ffOverride ? "granted" : "revoked"}${minTier ? ` (default tier: ${minTier})` : ""}`
                  : `Included with ${minTier} plan and higher`;
                return (
                  <div key={f.key} data-testid={`feature-${f.key}`} title={tooltip}
                       className={`flex items-center gap-2 text-sm border p-2.5 ${granted
                         ? (isOverridden ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-[#0F0F0F]")
                         : "border-[#E2E0D8] text-[#5C5C5C]"}`}>
                    {granted ? <Check className="w-4 h-4 text-green-700 shrink-0" /> : <X className="w-4 h-4 text-[#9C9A92] shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{f.label}</div>
                      {isOverridden && (
                        <div data-testid={`feature-${f.key}-override-tag`} className="text-[10px] text-[var(--brand-primary)] font-semibold uppercase tracking-widest mt-0.5">
                          Owner-{ffOverride ? "granted" : "revoked"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#5C5C5C] mt-2 italic">Hover a feature for details. Owner-granted perks appear with a red border; revoked perks show even if your plan would normally include them.</p>
          </div>
        )}
      </section>

      <section className="mt-8">
        <div className="label-eyebrow mb-3">Change plan</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {(plansToRender && plansToRender.length > 0 ? plansToRender : PLANS).map((p) => {
            const isFree = (p.name || "").toLowerCase() === "free";
            const monthlyLabel = p.monthly == null ? "Custom" : (p.monthly === 0 ? "Free" : `£${p.monthly}/mo`);
            const annualLabel = p.annual == null ? null : (p.annual === 0 ? null : `£${p.annual}/mo annual`);
            return (
            <div key={p.tier} className="border border-[#E2E0D8] p-5 relative">
              {p.tag && (
                <span className="absolute -top-2.5 left-3 bg-[#0F0F0F] text-[#FDFCF8] text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-1">
                  {p.tag}
                </span>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-xl">{p.name}</div>
                  <div className="text-xs text-[#5C5C5C] mt-0.5">{p.desc}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">{monthlyLabel}</div>
                  {annualLabel && <div className="text-xs text-[#5C5C5C]">{annualLabel}</div>}
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <div className="flex gap-2">
                  <button onClick={() => buy(p.tier, "monthly")} disabled={pending || redirecting || isFree || p.monthly == null} data-testid={`buy-${p.tier}-monthly`} className="btn-ink text-sm flex-1 disabled:opacity-40 disabled:cursor-not-allowed">{isFree ? "On Free plan" : "Monthly"}</button>
                  <button onClick={() => buy(p.tier, "annual")} disabled={pending || redirecting || isFree || p.annual == null || p.annual === 0} data-testid={`buy-${p.tier}-annual`} className="btn-terra text-sm flex-1 disabled:opacity-40 disabled:cursor-not-allowed">Annual</button>
                </div>
                {!isFree && (p.tier === "starter" || p.tier === "growth" || p.is_custom) && (
                  <button onClick={() => openSignContractModal(p.tier)} disabled={pending || redirecting} data-testid={`buy-${p.tier}-contract`} className="w-full text-xs font-bold uppercase tracking-wider py-2 border border-[#0F0F0F] bg-white text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition disabled:opacity-50">
                    ✍️ 12-Mo Contract ({p.annual ? `£${p.annual}/mo` : "Discounted"})
                  </button>
                )}
              </div>
            </div>
            );
          })}
          <div className="border border-[#0F0F0F] p-5 bg-[#0F0F0F]/5">
            <div>
              <div className="font-display font-bold text-xl flex items-center gap-1.5">Enterprise</div>
              <div className="text-xs text-[#5C5C5C] mt-0.5">Custom branding · your own domain · unlimited jobs/applicants/boosts · SLA-backed support · dedicated success manager.</div>
            </div>
            <div className="font-display font-bold mt-4">Custom pricing</div>
            <Link to="/contact-sales" data-testid="contact-sales-link" className="btn-terra text-sm w-full justify-center mt-3">
              Contact sales →
            </Link>
          </div>
        </div>
        <div className="mt-4">
          <label className="label-eyebrow block mb-1.5">Discount code (optional)</label>
          <input value={discount} onChange={(e) => setDiscount(e.target.value)}
            data-testid="discount-code"
            className="border border-[#0F0F0F] px-3 py-2 bg-transparent uppercase tracking-widest w-full sm:w-96"
            placeholder="e.g. PARTNER-LAUNCH-2026" />
        </div>
        {info && <div data-testid="billing-info" className="text-sm text-[#0F0F0F] mt-3 border border-[#0F0F0F] bg-[#0F0F0F]/5 px-3 py-2">{info}</div>}
        {error && <div data-testid="billing-error" className="text-sm text-[var(--brand-primary)] mt-3">{error}</div>}
      </section>

      <section className="mt-10">
        <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Receipt className="w-3 h-3" /> History</div>
        <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
          {(!billing?.history || billing.history.length === 0) && (
            <div className="bg-[#FDFCF8] p-5 text-[#5C5C5C] text-sm">No charges yet.</div>
          )}
          {(billing?.history || []).slice().reverse().map((h, i) => (
            <div key={`${h.at || "noat"}-${i}`} data-testid={`history-${i}`} className="bg-[#FDFCF8] p-4 flex items-center justify-between gap-3 text-sm">
              <div>
                <div className="font-semibold capitalize">{h.tier} · {h.cycle}</div>
                <div className="text-xs text-[#5C5C5C]">{new Date(h.at).toLocaleString()} · {h.status}{h.discount ? ` · -${h.discount.percent_off}% ${h.discount.code}` : ""}</div>
              </div>
              <div className="font-display font-bold">
                {h.status === "CANCELLED" ? "—" : `£${h.final_price ?? h.base_price ?? h.price ?? 0}`}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#5C5C5C] mt-3 italic">
          Payments securely processed by Stripe. Refunds available within 14 days, <strong>subject to owner approval</strong>.
        </p>
      </section>

      {myInvoices.length > 0 && (
        <section className="mt-10" data-testid="my-invoices">
          <div className="label-eyebrow flex items-center gap-1.5 mb-3"><FileText className="w-3 h-3" /> Your invoices</div>
          <div className="grid gap-3">
            {myInvoices.map((inv) => (
              <div key={inv.invoice_id} data-testid={`invoice-${inv.invoice_id}`} className="border border-[#E2E0D8] bg-[#FDFCF8] p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] px-1.5 py-0.5">{inv.status}</span>
                      <span className="font-mono text-xs">{inv.invoice_number}</span>
                    </div>
                    <div className="font-display font-bold text-lg mt-1">£{(inv.total_pence/100).toFixed(2)} — {inv.instalments_count} instalment{inv.instalments_count === 1 ? "" : "s"}</div>
                    <div className="text-xs text-[#5C5C5C]">Due {inv.due_at?.slice(0,10)}</div>
                  </div>
                  <button onClick={() => downloadInvoice(inv.invoice_id)} data-testid={`download-invoice-${inv.invoice_id}`} className="btn-ghost text-sm">
                    <Download className="w-4 h-4" /> Download PDF
                  </button>
                </div>
                {inv.instalments?.length > 1 && (
                  <div className="mt-3 grid gap-1.5 text-xs">
                    {inv.instalments.map((ins) => (
                      <div key={ins.index} className="flex items-center justify-between gap-2 border-b border-[#E2E0D8] py-1.5 flex-wrap">
                        <span>#{ins.index} · £{(ins.amount_pence/100).toFixed(2)} · due {ins.due_at?.slice(0,10)}</span>
                        <span className="flex items-center gap-3">
                          {ins.status !== "paid" && ins.stripe_payment_link_url && (
                            <a href={ins.stripe_payment_link_url} target="_blank" rel="noopener noreferrer"
                               data-testid={`pay-instalment-${inv.invoice_id}-${ins.index}`}
                               className="text-[10px] uppercase tracking-widest font-semibold bg-[var(--brand-primary)] text-white px-2 py-1 hover:bg-[#B83A28]">
                              Pay now
                            </a>
                          )}
                          <span className={`uppercase tracking-widest text-[10px] font-semibold ${ins.status === "paid" ? "text-green-700" : "text-[#5C5C5C]"}`}>{ins.status}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {inv.status !== "paid" && inv.status !== "void" && billing?.bank_fields && (
                  <div className="mt-4 border border-amber-600/30 bg-amber-50/50 p-3 text-xs" data-testid="bacs-payment-details">
                    <div className="font-semibold uppercase tracking-wider text-amber-900 flex items-center gap-1.5 mb-1.5">
                      🏦 Pay via BACS / Direct Bank Transfer
                    </div>
                    <div className="space-y-1 text-[#3a3a3a]">
                      {billing.bank_fields.payment_instructions && <div><strong>Instructions:</strong> {billing.bank_fields.payment_instructions}</div>}
                      {billing.bank_fields.bank_name && <div><strong>Bank:</strong> {billing.bank_fields.bank_name}</div>}
                      {billing.bank_fields.account_number && <div><strong>Account Number:</strong> {billing.bank_fields.account_number}</div>}
                      {billing.bank_fields.sort_code && <div><strong>Sort Code:</strong> {billing.bank_fields.sort_code}</div>}
                      {billing.bank_fields.iban && <div><strong>IBAN:</strong> {billing.bank_fields.iban}</div>}
                      {billing.bank_fields.bank_details && <div className="whitespace-pre-line mt-1.5 border-t border-[#E2E0D8] pt-1.5 font-mono">{billing.bank_fields.bank_details}</div>}
                      <div className="mt-2 text-[10px] uppercase tracking-wider text-[#5C5C5C]">
                        * Please reference invoice number <strong>{inv.invoice_number}</strong> when making the bank transfer.
                      </div>
                    </div>
                  </div>
                )}
                {inv.notes && <div className="mt-3 text-xs text-[#5C5C5C] italic">{inv.notes}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {contracts.length > 0 && (
        <section className="mt-10" data-testid="my-contracts">
          <div className="label-eyebrow flex items-center gap-1.5 mb-3"><FileText className="w-3 h-3" /> Your contracts</div>
          <div className="grid gap-3">
            {contracts.map((ct) => (
              <div key={ct.contract_id} data-testid={`contract-${ct.contract_id}`} className="border border-[#E2E0D8] bg-[#FDFCF8] p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] bg-[#0F0F0F] text-white px-1.5 py-0.5">{ct.status}</span>
                      <span className="font-mono text-xs">{ct.contract_id}</span>
                    </div>
                    <div className="font-display font-bold text-lg mt-1 capitalize">{ct.tier} Plan — 12-Month Contract</div>
                    <div className="text-xs text-[#5C5C5C]">
                      Signed by <strong>{ct.signed_by_name}</strong> {ct.signer_title ? `(${ct.signer_title})` : ""} on {new Date(ct.signed_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-[#5C5C5C] mt-1">
                      Term: {new Date(ct.start_date).toLocaleDateString()} to {new Date(ct.end_date).toLocaleDateString()} · £{(ct.monthly_price_pence/100).toFixed(2)}/mo
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setSelectedViewContract(ct)} data-testid={`view-contract-btn-${ct.contract_id}`} className="btn-ghost text-sm">
                      <FileText className="w-4 h-4" /> View Contract Terms
                    </button>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/contracts/${ct.contract_id}/pdf`}
                      data-testid={`download-contract-pdf-${ct.contract_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost text-sm flex items-center gap-1.5"
                    >
                      <Download className="w-4 h-4" /> Download PDF
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Contract Signature Modal */}
      {showContractModal && contractDraft && (
        <div className="fixed inset-0 bg-[#0F0F0F]/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#FDFCF8] border-2 border-[#0F0F0F] w-full max-w-3xl p-6 sm:p-8 max-h-[90vh] flex flex-col shadow-2xl relative rounded-none text-left">
            <button onClick={() => setShowContractModal(false)} className="absolute top-4 right-4 text-[#5C5C5C] hover:text-[#0F0F0F]">
              <X className="w-6 h-6" />
            </button>
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="label-eyebrow flex items-center gap-1.5 text-[var(--brand-primary)]">
                <FileText className="w-4 h-4" /> Binding Contract Agreement
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/contracts/draft/pdf?tier=${contractTier}`}
                data-testid="download-draft-contract-pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold uppercase tracking-wider py-1 px-2.5 border border-[#0F0F0F] bg-white text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF for Physical Signage
              </a>
            </div>
            <h2 className="font-display font-black text-2xl mt-1 tracking-tight">
              12-Month Contract: {contractTier.toUpperCase()} Plan
            </h2>
            <p className="text-xs text-[#5C5C5C] mt-2 leading-relaxed">
              Please review the binding agreement below. Enter your full name and job title to digitally sign and execute this 12-Month contract.
            </p>

            <div className="mt-4 flex-1 overflow-y-auto border border-[#E2E0D8] p-4 bg-[#F3F2EB] font-mono text-xs whitespace-pre-wrap leading-relaxed max-h-[40vh]">
              {contractDraft.contract_text}
            </div>

            <div className="mt-6 border border-[#E2E0D8] p-4 bg-[#F3F2EB]">
              <div className="label-eyebrow text-[10px] text-[#5C5C5C] mb-2">Choose Signature Method</div>
              <div className="flex gap-2 border-b border-[#E2E0D8] pb-3 mb-4 flex-wrap">
                {[
                  { v: "digital", t: "✍️ Digital Signature (Type name)" },
                  { v: "physical_sig", t: "📷 Upload Signature Image" },
                  { v: "contract_doc", t: "📄 Upload Signed Contract" }
                ].map(({ v, t }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setSignatureType(v)}
                    data-testid={`sig-method-${v}`}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border ${signatureType === v ? "bg-[#0F0F0F] text-white border-[#0F0F0F]" : "bg-white text-[#0F0F0F] border-[#E2E0D8] hover:border-[#0F0F0F]"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                    Full Name (Signer Name) <span className="text-[var(--brand-primary)]">*</span>
                  </label>
                  <input
                    type="text"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    data-testid="contract-signature-name"
                    placeholder="e.g. Jane Doe"
                    className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                    Job Title / Role (Optional)
                  </label>
                  <input
                    type="text"
                    value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                    data-testid="contract-signer-title"
                    placeholder="e.g. HR Director"
                    className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                    Date of Birth (Must be 18+) <span className="text-[var(--brand-primary)]">*</span>
                  </label>
                  <input
                    type="date"
                    value={contractDob}
                    onChange={(e) => setContractDob(e.target.value)}
                    data-testid="contract-dob-input"
                    className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F]"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                    Discount Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={contractDiscountCode}
                    onChange={(e) => setContractDiscountCode(e.target.value.toUpperCase())}
                    data-testid="contract-discount-input"
                    placeholder="e.g. 3FREE"
                    className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)] text-[#0F0F0F] uppercase tracking-widest"
                  />
                </div>

                {signatureType === "physical_sig" && (
                  <div className="col-span-2 border-t border-[#E2E0D8] pt-3">
                    <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                      Upload your Hand-Written Physical Signature Image <span className="text-[var(--brand-primary)]">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadContractFile(e, "signature")}
                      data-testid="contract-sig-file-input"
                      className="w-full text-xs font-mono"
                    />
                    {uploading && <div className="text-xs text-[#5C5C5C] mt-1">Uploading file...</div>}
                    {uploadedSignatureUrl && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-green-700">Signature Uploaded Successfully:</div>
                        <img
                          src={uploadedSignatureUrl}
                          alt="Signature Preview"
                          data-testid="sig-preview-img"
                          className="mt-1 border border-[#0F0F0F] bg-white max-h-16 object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}

                {signatureType === "contract_doc" && (
                  <div className="col-span-2 border-t border-[#E2E0D8] pt-3">
                    <label className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] block mb-1">
                      Upload Completed & Signed Contract Document (PDF or Image) <span className="text-[var(--brand-primary)]">*</span>
                    </label>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => handleUploadContractFile(e, "contract")}
                      data-testid="contract-doc-file-input"
                      className="w-full text-xs font-mono"
                    />
                    {uploading && <div className="text-xs text-[#5C5C5C] mt-1">Uploading file...</div>}
                    {uploadedContractUrl && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-green-700 flex items-center gap-1">
                          ✓ Contract Document Uploaded successfully!
                        </div>
                        <a
                          href={uploadedContractUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="uploaded-contract-doc-link"
                          className="text-xs text-blue-700 underline mt-1 block font-mono truncate"
                        >
                          {uploadedContractUrl}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowContractModal(false)}
                className="px-5 py-2 text-sm font-semibold border border-[#0F0F0F] hover:bg-[#0F0F0F]/5 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSignContract}
                disabled={signing || !signatureName.trim()}
                data-testid="contract-submit-btn"
                className="bg-[var(--brand-primary)] text-white px-6 py-2 text-sm font-semibold hover:bg-[#B83A28] disabled:opacity-40 transition flex items-center gap-2"
              >
                {signing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Signing…</>
                ) : (
                  "Sign & Activate Contract"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Viewer Modal */}
      {selectedViewContract && (
        <div className="fixed inset-0 bg-[#0F0F0F]/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#FDFCF8] border-2 border-[#0F0F0F] w-full max-w-3xl p-6 sm:p-8 max-h-[90vh] flex flex-col shadow-2xl relative rounded-none text-left">
            <button onClick={() => setSelectedViewContract(null)} className="absolute top-4 right-4 text-[#5C5C5C] hover:text-[#0F0F0F]">
              <X className="w-6 h-6" />
            </button>
            <div className="label-eyebrow flex items-center gap-1.5 text-green-700">
              <ShieldCheck className="w-4 h-4" /> Signed Agreement
            </div>
            <h2 className="font-display font-black text-2xl mt-1 tracking-tight">
              Executed 12-Month Contract
            </h2>
            <div className="mt-2 text-xs text-[#5C5C5C] flex flex-wrap gap-x-4 gap-y-1">
              <span>Contract ID: <strong>{selectedViewContract.contract_id}</strong></span>
              <span>Signed At: <strong>{new Date(selectedViewContract.signed_at).toLocaleString()}</strong></span>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto border border-[#E2E0D8] p-4 bg-[#F3F2EB] font-mono text-xs whitespace-pre-wrap leading-relaxed max-h-[50vh]">
              {selectedViewContract.contract_text}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedViewContract(null)}
                className="btn-ink px-6 py-2 text-sm font-semibold"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
